import { useEffect, useRef, type ReactNode } from 'react';
import { LM, sideLandmarks } from '@erectus/shared';
import type { Landmark, Side } from '@erectus/shared';

/**
 * Skeleton overlay.
 *
 * Drawn purely from landmark numbers on a transparent canvas above the video.
 * The camera frame itself is never drawn here, so there is no path from pixels
 * to an exportable bitmap (CLAUDE.md "하지 말 것" — no frame export).
 *
 * Coordinate mapping matters more than it looks. Landmarks are normalized to
 * the *video* frame, but the video is displayed with `object-fit: cover`, which
 * scales to fill and crops the overflow. Drawing straight onto a fixed-size
 * canvas puts the skeleton several centimetres away from the body on a portrait
 * phone. The transform below reproduces cover exactly.
 */
const BONES: Array<[number, number]> = [
  [LM.NOSE, LM.LEFT_EAR],
  [LM.NOSE, LM.RIGHT_EAR],
  [LM.LEFT_EAR, LM.LEFT_SHOULDER],
  [LM.RIGHT_EAR, LM.RIGHT_SHOULDER],
  [LM.LEFT_SHOULDER, LM.RIGHT_SHOULDER],
  [LM.LEFT_SHOULDER, LM.LEFT_HIP],
  [LM.RIGHT_SHOULDER, LM.RIGHT_HIP],
  [LM.LEFT_HIP, LM.RIGHT_HIP],
  [LM.LEFT_SHOULDER, LM.LEFT_ELBOW],
  [LM.LEFT_ELBOW, LM.LEFT_WRIST],
  [LM.LEFT_WRIST, LM.LEFT_INDEX],
  [LM.RIGHT_SHOULDER, LM.RIGHT_ELBOW],
  [LM.RIGHT_ELBOW, LM.RIGHT_WRIST],
  [LM.RIGHT_WRIST, LM.RIGHT_INDEX],
  [LM.LEFT_HIP, LM.LEFT_KNEE],
  [LM.LEFT_KNEE, LM.LEFT_ANKLE],
  [LM.RIGHT_HIP, LM.RIGHT_KNEE],
  [LM.RIGHT_KNEE, LM.RIGHT_ANKLE],
];

/** Every joint we draw a dot for. */
const JOINTS = [
  LM.NOSE,
  LM.LEFT_EAR,
  LM.RIGHT_EAR,
  LM.LEFT_SHOULDER,
  LM.RIGHT_SHOULDER,
  LM.LEFT_ELBOW,
  LM.RIGHT_ELBOW,
  LM.LEFT_WRIST,
  LM.RIGHT_WRIST,
  LM.LEFT_INDEX,
  LM.RIGHT_INDEX,
  LM.LEFT_HIP,
  LM.RIGHT_HIP,
  LM.LEFT_KNEE,
  LM.RIGHT_KNEE,
  LM.LEFT_ANKLE,
  LM.RIGHT_ANKLE,
];

const MIN_VISIBILITY = 0.4;

export interface SkeletonOverlayProps {
  landmarks: readonly Landmark[];
  /** The <video> the landmarks came from; needed for the cover transform. */
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** Highlights the joints the measurement actually uses. */
  side?: Side;
  color?: string;
  /**
   * Must match the video element's object-fit, or the dots land in the wrong
   * place. `cover` is the default because the capture is 3:4 and the viewport
   * is taller, so cover crops horizontally only and keeps the vertical field of
   * view the metrics depend on.
   */
  fit?: 'cover' | 'contain';
}

export function SkeletonOverlay({
  landmarks,
  videoRef,
  side,
  color = '#4ade80',
  fit = 'cover',
}: SkeletonOverlayProps): ReactNode {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Match the canvas backing store to the displayed box, at device resolution.
    const box = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.max(1, Math.round(box.width * dpr));
    const height = Math.max(1, Math.round(box.height * dpr));
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
    ctx.clearRect(0, 0, width, height);
    if (landmarks.length === 0) return;

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (vw === 0 || vh === 0) return;

    // Reproduce the element's object-fit exactly: cover scales by the larger
    // ratio and crops, contain scales by the smaller and letterboxes.
    const scale =
      fit === 'cover' ? Math.max(width / vw, height / vh) : Math.min(width / vw, height / vh);
    const drawW = vw * scale;
    const drawH = vh * scale;
    const offsetX = (width - drawW) / 2;
    const offsetY = (height - drawH) / 2;
    const project = (lm: Landmark): [number, number] => [
      offsetX + lm.x * drawW,
      offsetY + lm.y * drawH,
    ];

    const measured = side ? sideLandmarks(side) : null;
    const measuredSet = new Set<number>(
      measured
        ? [measured.ear, measured.shoulder, measured.hip, measured.knee, measured.ankle, LM.NOSE]
        : [],
    );

    const unit = Math.max(1, Math.min(width, height) / 100);

    // Bones first, so the joint dots sit on top.
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const [a, b] of BONES) {
      const from = landmarks[a];
      const to = landmarks[b];
      if (!from || !to) continue;
      if (from.visibility < MIN_VISIBILITY || to.visibility < MIN_VISIBILITY) continue;
      const [x1, y1] = project(from);
      const [x2, y2] = project(to);
      const key = measuredSet.has(a) && measuredSet.has(b);
      // A dark casing under the stroke keeps it readable on a bright wall.
      ctx.strokeStyle = 'rgba(15,23,42,0.45)';
      ctx.lineWidth = unit * (key ? 1.5 : 1.1);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
      ctx.strokeStyle = key ? color : 'rgba(255,255,255,0.65)';
      ctx.lineWidth = unit * (key ? 0.9 : 0.55);
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    }

    // Joints: the vertices the angles are computed from, so they are visible.
    for (const index of JOINTS) {
      const point = landmarks[index];
      if (!point || point.visibility < MIN_VISIBILITY) continue;
      const [x, y] = project(point);
      const key = measuredSet.has(index);
      const radius = unit * (key ? 1.5 : 0.95);

      ctx.beginPath();
      ctx.arc(x, y, radius + unit * 0.35, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(15,23,42,0.55)';
      ctx.fill();

      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fillStyle = key ? color : 'rgba(255,255,255,0.9)';
      ctx.fill();

      if (key) {
        // Hollow centre marks the landmarks the measurement depends on.
        ctx.beginPath();
        ctx.arc(x, y, radius * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(15,23,42,0.85)';
        ctx.fill();
      }
    }
  }, [landmarks, side, color, videoRef, fit]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className="pointer-events-none absolute inset-0 size-full"
    />
  );
}
